import { createLogger } from '@/lib/logger'
import { desktopSecureStorage, getPlatform, isTauri } from '@/lib/utils/tauri'
import { useOrganizationStore } from '@/store/organization.store'
import { env } from '@/utils/env'

/**
 * Time Entries API service using BaseApi pattern
 * Includes approval workflow methods
 */

import {
  BulkApprovalResponse,
  CreateTimeEntry,
  PaginatedResponse,
  PaginationParams,
  SplitTimeEntryInput,
  SwitchTimerItemInput,
  TimeConflictGroup,
  TimeEntry,
  TimeEntryApprovalHistory,
  UpdateTimeEntryInput,
} from '@scrambl/shared'

import { BaseApi } from './base-api'
import { createSingletonApi } from './singleton-factory'
import { fetchAllPages } from './paginate'

const logger = createLogger('lib/api/time-entries.api')

class TimeEntryApiService extends BaseApi<
  TimeEntry,
  CreateTimeEntry,
  UpdateTimeEntryInput
> {
  constructor() {
    super({
      baseEndpoint: '/time-entries',
      responseNormalization: true,
    })
  }

  // Alias methods for backward compatibility
  async getTimeEntries(
    params: Partial<PaginationParams> = {},
  ): Promise<PaginatedResponse<TimeEntry>> {
    return this.list(params)
  }

  async getAllTimeEntries(
    params: Partial<PaginationParams> & Record<string, unknown> = {},
    pageSize = 200,
  ): Promise<PaginatedResponse<TimeEntry>> {
    let lastMeta: PaginatedResponse<TimeEntry>['meta'] | undefined
    const data = await fetchAllPages(async (page, limit) => {
      const response = await this.getTimeEntries({ ...params, page, limit })
      lastMeta = response.meta
      return response
    }, pageSize)

    return {
      data,
      meta: {
        ...lastMeta,
        totalItems: data.length,
        currentPage: 1,
        itemsPerPage: Math.max(data.length, 1),
        totalPages: data.length > 0 ? 1 : 0,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    }
  }

  async getTimeEntry(id: string): Promise<TimeEntry> {
    return this.findById(id)
  }

  async getTaskTimeTotal(
    taskId: string,
  ): Promise<{ taskId: string; totalHours: number; entryCount: number }> {
    return this.makeRequest<{
      taskId: string
      totalHours: number
      entryCount: number
    }>(`${this.config.baseEndpoint}/tasks/${taskId}/my-total`)
  }

  async createTimeEntry(data: CreateTimeEntry): Promise<TimeEntry> {
    return this.create(data)
  }

  async updateTimeEntry(
    id: string,
    data: UpdateTimeEntryInput,
  ): Promise<TimeEntry> {
    return this.update(id, data)
  }

  async deleteTimeEntry(id: string): Promise<void> {
    return this.delete(id)
  }

  // Timer-specific methods
  async getActiveTimer(): Promise<TimeEntry | null> {
    try {
      const response = await this.makeRequest<TimeEntry | null>(
        `${this.config.baseEndpoint}/active`,
      )
      if (!response || typeof response !== 'object' || !('id' in response)) {
        return null
      }
      return response
    } catch (error) {
      logger.error('[TimeEntryApi] Error fetching active timer:', error)
      return null
    }
  }

  // Overlap (double-tracking) conflicts for the current user.
  async getMyTimeConflicts(params: {
    startDate: string
    endDate: string
  }): Promise<{ groups: TimeConflictGroup[] }> {
    const query = new URLSearchParams({
      startDate: params.startDate,
      endDate: params.endDate,
    }).toString()
    return this.makeRequest<{ groups: TimeConflictGroup[] }>(
      `${this.config.baseEndpoint}/conflicts?${query}`,
    )
  }

  // Overlap conflicts for a specific team member (manager view).
  async getTeamTimeConflicts(params: {
    userId: string
    startDate: string
    endDate: string
  }): Promise<{ groups: TimeConflictGroup[] }> {
    const query = new URLSearchParams({
      userId: params.userId,
      startDate: params.startDate,
      endDate: params.endDate,
    }).toString()
    return this.makeRequest<{ groups: TimeConflictGroup[] }>(
      `${this.config.baseEndpoint}/team/conflicts?${query}`,
    )
  }

  async updateHeartbeat(
    id: string,
  ): Promise<{ success: boolean; active: boolean }> {
    return this.makeRequest<{ success: boolean; active: boolean }>(
      `${this.config.baseEndpoint}/${id}/heartbeat`,
      {
        method: 'PUT',
      },
    )
  }

  async pauseTimer(id: string): Promise<TimeEntry> {
    const response = await this.makeRequest<TimeEntry | { data: TimeEntry }>(
      `${this.config.baseEndpoint}/${id}/pause`,
      {
        method: 'PUT',
      },
    )
    return this.normalizeResponse(response)
  }

  async resumeTimer(id: string): Promise<TimeEntry> {
    const response = await this.makeRequest<TimeEntry | { data: TimeEntry }>(
      `${this.config.baseEndpoint}/${id}/resume`,
      {
        method: 'PUT',
      },
    )
    return this.normalizeResponse(response)
  }

  async splitTimeEntry(
    id: string,
    data: SplitTimeEntryInput,
  ): Promise<TimeEntry[]> {
    const response = await this.makeRequest<
      TimeEntry[] | { data: TimeEntry[] }
    >(`${this.config.baseEndpoint}/${id}/split`, {
      method: 'POST',
      body: data,
    })

    if (Array.isArray(response)) {
      return response
    }
    return response.data || []
  }

  async switchTimerItem(
    id: string,
    data: SwitchTimerItemInput,
  ): Promise<TimeEntry> {
    const response = await this.makeRequest<TimeEntry | { data: TimeEntry }>(
      `${this.config.baseEndpoint}/${id}/switch-item`,
      {
        method: 'PUT',
        body: data,
      },
    )

    return this.normalizeResponse(response)
  }

  // =====================
  // Approval Workflow Methods
  // =====================

  async submitForApproval(id: string): Promise<TimeEntry> {
    const response = await this.makeRequest<TimeEntry | { data: TimeEntry }>(
      `${this.config.baseEndpoint}/${id}/submit`,
      {
        method: 'PUT',
      },
    )
    return this.normalizeResponse(response)
  }

  async approveEntry(id: string, comment?: string): Promise<TimeEntry> {
    const response = await this.makeRequest<TimeEntry | { data: TimeEntry }>(
      `${this.config.baseEndpoint}/${id}/approve`,
      {
        method: 'PUT',
        body: comment ? { comment } : undefined,
      },
    )
    return this.normalizeResponse(response)
  }

  async rejectEntry(id: string, reason: string): Promise<TimeEntry> {
    const response = await this.makeRequest<TimeEntry | { data: TimeEntry }>(
      `${this.config.baseEndpoint}/${id}/reject`,
      {
        method: 'PUT',
        body: { reason },
      },
    )
    return this.normalizeResponse(response)
  }

  async requestChanges(id: string, comment: string): Promise<TimeEntry> {
    const response = await this.makeRequest<TimeEntry | { data: TimeEntry }>(
      `${this.config.baseEndpoint}/${id}/request-changes`,
      {
        method: 'PUT',
        body: { comment },
      },
    )
    return this.normalizeResponse(response)
  }

  async recallSubmission(id: string): Promise<TimeEntry> {
    const response = await this.makeRequest<TimeEntry | { data: TimeEntry }>(
      `${this.config.baseEndpoint}/${id}/recall`,
      {
        method: 'PUT',
      },
    )
    return this.normalizeResponse(response)
  }

  async getApprovalHistory(id: string): Promise<TimeEntryApprovalHistory[]> {
    const response = await this.makeRequest<
      TimeEntryApprovalHistory[] | { data: TimeEntryApprovalHistory[] }
    >(`${this.config.baseEndpoint}/${id}/history`)
    if (Array.isArray(response)) {
      return response
    }
    return response.data || []
  }

  // =====================
  // Bulk Approval Methods
  // =====================

  async bulkSubmit(
    timeEntryIds: string[],
    comment?: string,
  ): Promise<BulkApprovalResponse> {
    const response = await this.makeRequest<BulkApprovalResponse>(
      `${this.config.baseEndpoint}/bulk-submit`,
      {
        method: 'POST',
        body: { timeEntryIds, comment },
      },
    )
    return response
  }

  async bulkApprove(
    timeEntryIds: string[],
    comment?: string,
    draftEntryIds?: string[],
  ): Promise<BulkApprovalResponse> {
    const response = await this.makeRequest<BulkApprovalResponse>(
      `${this.config.baseEndpoint}/bulk-approve`,
      {
        method: 'POST',
        body: { timeEntryIds, comment, draftEntryIds },
      },
    )
    return response
  }

  async bulkReject(
    timeEntryIds: string[],
    reason: string,
  ): Promise<BulkApprovalResponse> {
    const response = await this.makeRequest<BulkApprovalResponse>(
      `${this.config.baseEndpoint}/bulk-reject`,
      {
        method: 'POST',
        body: { timeEntryIds, reason },
      },
    )
    return response
  }

  // =====================
  // Admin Status Change Methods
  // =====================

  async adminChangeStatus(
    id: string,
    status: string,
    comment?: string,
  ): Promise<TimeEntry> {
    const response = await this.makeRequest<TimeEntry | { data: TimeEntry }>(
      `${this.config.baseEndpoint}/${id}/admin-status`,
      {
        method: 'PUT',
        body: { status, comment },
      },
    )
    return this.normalizeResponse(response)
  }

  async bulkAdminChangeStatus(
    timeEntryIds: string[],
    status: string,
    comment?: string,
  ): Promise<BulkApprovalResponse> {
    const response = await this.makeRequest<BulkApprovalResponse>(
      `${this.config.baseEndpoint}/bulk-admin-status`,
      {
        method: 'POST',
        body: { timeEntryIds, status, comment },
      },
    )
    return response
  }

  async adminChangeUserPeriodStatus(
    userId: string,
    payPeriodId: string,
    status: string,
    comment?: string,
  ): Promise<BulkApprovalResponse> {
    const response = await this.makeRequest<BulkApprovalResponse>(
      `${this.config.baseEndpoint}/user-period-admin-status`,
      {
        method: 'POST',
        body: { userId, payPeriodId, status, comment },
      },
    )
    return response
  }

  // =====================
  // Team/Admin View Methods
  // =====================

  private buildTeamEntriesQueryParams(params: {
    memberId?: string
    statusTier?: string
    status?: string
    projectId?: string
    entityId?: string
    taskId?: string
    moduleType?: string
    description?: string
    billable?: boolean
    startDate?: string
    endDate?: string
    includeActiveTimers?: boolean
    page?: number
    limit?: number
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  }): string {
    const queryParams = new URLSearchParams()
    if (params.memberId) queryParams.append('memberId', params.memberId)
    if (params.statusTier) queryParams.append('statusTier', params.statusTier)
    if (params.status) queryParams.append('status', params.status)
    if (params.projectId) queryParams.append('projectId', params.projectId)
    if (params.entityId) queryParams.append('entityId', params.entityId)
    if (params.taskId) queryParams.append('taskId', params.taskId)
    if (params.moduleType) queryParams.append('moduleType', params.moduleType)
    if (params.description)
      queryParams.append('description', params.description)
    if (params.billable !== undefined)
      queryParams.append('billable', String(params.billable))
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    if (params.includeActiveTimers !== undefined)
      queryParams.append(
        'includeActiveTimers',
        String(params.includeActiveTimers),
      )
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder)
    return queryParams.toString()
  }

  async getTeamEntries(
    params: {
      memberId?: string
      statusTier?: string
      status?: string
      projectId?: string
      entityId?: string
      taskId?: string
      moduleType?: string
      description?: string
      billable?: boolean
      startDate?: string
      endDate?: string
      page?: number
      limit?: number
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
    } = {},
  ): Promise<{
    data: TimeEntry[]
    total: number
    totals?: {
      totalHours: number
      billableHours: number
      nonBillableHours: number
      totalBillableAmount: number
      totalCostAmount: number
    }
  }> {
    const queryString = this.buildTeamEntriesQueryParams(params)
    const url = `${this.config.baseEndpoint}/team${queryString ? `?${queryString}` : ''}`
    const response = await this.makeRequest<{
      data: TimeEntry[]
      total: number
      totals?: {
        totalHours: number
        billableHours: number
        nonBillableHours: number
        totalBillableAmount: number
        totalCostAmount: number
      }
    }>(url)
    return response
  }

  async getAllTeamEntries(
    params: Parameters<TimeEntryApiService['getTeamEntries']>[0] = {},
    pageSize = 200,
  ): Promise<Awaited<ReturnType<TimeEntryApiService['getTeamEntries']>>> {
    if (!Number.isInteger(pageSize) || pageSize <= 0) {
      throw new Error('pageSize must be a positive integer')
    }

    const data: TimeEntry[] = []
    let page = 1
    const maxPages = 200
    let total = 0
    let totals:
      | Awaited<ReturnType<TimeEntryApiService['getTeamEntries']>>['totals']
      | undefined

    while (page <= maxPages) {
      const response = await this.getTeamEntries({
        ...params,
        page,
        limit: pageSize,
      })
      data.push(...response.data)
      total = response.total
      totals = response.totals

      if (response.data.length < pageSize || data.length >= total) {
        return { data, total, totals }
      }
      if (page === maxPages) {
        throw new Error(
          `Time-entry pagination exceeded the ${maxPages}-page safety bound; refusing to return a truncated result`,
        )
      }
      page += 1
    }

    return { data, total, totals }
  }

  async getTeamSummary(
    params: {
      memberId?: string
      statusTier?: string
      status?: string
      projectId?: string
      entityId?: string
      taskId?: string
      moduleType?: string
      description?: string
      billable?: boolean
      startDate?: string
      endDate?: string
      includeActiveTimers?: boolean
    } = {},
  ): Promise<{
    members: Array<{
      userId: string
      name: string
      email: string
      totalHours: number
      billableHours: number
      approvedHours: number
      pendingHours: number
      draftHours: number
      rejectedHours: number
      entries: number
      hasActiveTimer: boolean
      activeHours: number
      currentActivity?: string
    }>
    totals: {
      totalHours: number
      billableHours: number
      approvedHours: number
      pendingHours: number
      draftHours: number
      rejectedHours: number
      totalEntries: number
      uniqueMembers: number
    }
  }> {
    const queryString = this.buildTeamEntriesQueryParams(params)
    const url = `${this.config.baseEndpoint}/team/summary${queryString ? `?${queryString}` : ''}`
    return this.makeRequest(url)
  }

  async getTeamFilterOptions(): Promise<{
    projects: Array<{
      id: string
      name: string
      entityId: string | null
      entityName: string | null
    }>
    entities: Array<{ id: string; name: string }>
  }> {
    return this.makeRequest(`${this.config.baseEndpoint}/team/filter-options`)
  }

  async exportTimeReport(body: {
    format: 'csv' | 'xlsx' | 'pdf'
    dateRange?: { start?: string; end?: string }
    statusTier?: string | null
    statusFilter?: string[]
    projectScope?: { projectId: string }
    filters?: {
      memberIds?: string[]
      projectIds?: string[]
      roleIds?: string[]
      billable?: boolean | null
      moduleTypes?: string[]
      entityIds?: string[]
      taskId?: string
      description?: string
      workItemType?: 'PROJECT' | 'TASK' | 'TASK_GROUP'
      workItemIds?: string[]
    }
    groupBy?: string | null
    columns?: string[]
    sortBy?: string | null
    sortDirection?: 'asc' | 'desc'
  }): Promise<Blob | { jobId: string; status: string }> {
    const acceptType =
      body.format === 'csv'
        ? 'text/csv'
        : body.format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

    const { currentOrganization } = useOrganizationStore.getState()
    const headers: Record<string, string> = {
      Accept: acceptType,
      'Content-Type': 'application/json',
      'x-platform': getPlatform(),
    }

    if (currentOrganization?.id) {
      headers['x-organization-id'] = currentOrganization.id
      if (currentOrganization.slug) {
        headers['x-organization-slug'] = currentOrganization.slug
      }
    }

    if (isTauri()) {
      try {
        const tokens = await desktopSecureStorage.getTokens()
        if (tokens?.accessToken) {
          headers.Authorization = `Bearer ${tokens.accessToken}`
        }
      } catch (error) {
        logger.error('[TimeEntryApi] Failed to get desktop auth token:', error)
      }
    }

    const response = await fetch(`${env.apiUrl}/reports/time/export`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    })

    if (response.status === 202) {
      return response.json() as Promise<{ jobId: string; status: string }>
    }

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(
        `Request failed: ${response.status} ${response.statusText} - ${errorText}`,
      )
    }

    return response.blob()
  }

  async getTimeReportExportJob(jobId: string): Promise<{
    id: string
    status: 'queued' | 'processing' | 'completed' | 'failed'
    filename?: string
    error?: string
  }> {
    return this.makeRequest(`/reports/time/export/jobs/${jobId}`)
  }

  async downloadTimeReportExportJob(jobId: string): Promise<Blob> {
    return this.makeBlobRequest(`/reports/time/export/jobs/${jobId}/download`, {
      acceptType: 'application/octet-stream',
    })
  }

  async getPendingApprovals(
    params: {
      memberId?: string
      projectId?: string
      startDate?: string
      endDate?: string
      page?: number
      limit?: number
    } = {},
  ): Promise<{ data: TimeEntry[]; total: number }> {
    const queryParams = new URLSearchParams()
    if (params.memberId) queryParams.append('memberId', params.memberId)
    if (params.projectId) queryParams.append('projectId', params.projectId)
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())

    const url = `${this.config.baseEndpoint}/team/pending${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await this.makeRequest<{
      data: TimeEntry[]
      total: number
    }>(url)
    return response
  }
}

// Export singleton instance using factory
export const timeEntryApi = createSingletonApi(TimeEntryApiService)

// Export class for typing
export type { TimeEntryApiService }
